import { useMemo } from 'react'
import { useParams } from 'react-router'

import { useFetchQuote } from '@features/invoices/hooks/useFetchQuote'
import type { DepositSettlement } from '@features/invoices/types/invoices/depositSettlement'
import type { InvoiceTotals } from '@features/invoices/types/invoices/invoiceTotals'
import { getQuoteDepositInvoice } from '@features/invoices/utils/depositInvoices/getQuoteDepositInvoice'
import { subtractDepositFromLineTotals } from '@features/invoices/utils/depositInvoices/subtractDepositFromLineTotals'

import type { InvoiceEditorUrlParams } from '../../../../../../../../../types/invoiceEditorUrlParams'
import { InvoiceEditorVariant } from '../../../../../../../enums/invoiceEditorVariant'
import { useFetchSalesDocumentDraft } from '../../../../../../../hooks/useFetchSalesDocumentDraft'
import { useInvoiceEditorDocumentType } from '../../../../../../../hooks/useInvoiceEditorDocumentType'
import { useInvoiceEditorVariant } from '../../../../../../../hooks/useInvoiceEditorVariant'
import { getQuoteIdFromDraft } from '../../../../../../../utils/invoiceDraft/getQuoteIdFromDraft'
import { variantsWithDepositSettlement } from '../constants/variantsWithDepositSettlement'

export const useFinalInvoiceDepositSettlement = (lineTotals: InvoiceTotals) => {
  const { grossTotal, netTotal, taxSummary, taxTotal } = lineTotals
  const { documentId } = useParams<InvoiceEditorUrlParams>()
  const { documentType } = useInvoiceEditorDocumentType()
  const { invoiceEditorVariant } = useInvoiceEditorVariant()

  const isFinalInvoice = variantsWithDepositSettlement.includes(invoiceEditorVariant)
  const isDraftEdit = invoiceEditorVariant === InvoiceEditorVariant.FinalInvoiceDraftEdit

  const { salesDocumentDraft, isLoading: isFetchingDraft } = useFetchSalesDocumentDraft(
    documentType,
    isFinalInvoice && isDraftEdit ? documentId : undefined,
  )

  const quoteId = isDraftEdit ? getQuoteIdFromDraft(salesDocumentDraft) : documentId

  const { quote, isLoading: isFetchingQuote } = useFetchQuote(isFinalInvoice ? quoteId : undefined)

  const depositSummary = getQuoteDepositInvoice(quote)?.summary

  const depositSettlement = useMemo((): DepositSettlement | undefined => {
    if (!depositSummary) {
      return undefined
    }

    return {
      amountDue: subtractDepositFromLineTotals({ grossTotal, netTotal, taxSummary, taxTotal }, depositSummary),
      depositGrossTotal: depositSummary.foreignGrossTotal,
    }
  }, [depositSummary, grossTotal, netTotal, taxSummary, taxTotal])

  return useMemo(
    () => ({
      depositSettlement,
      isLoading: isFinalInvoice && !depositSettlement && (isFetchingDraft || isFetchingQuote),
    }),
    [depositSettlement, isFetchingDraft, isFetchingQuote, isFinalInvoice],
  )
}
